import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Switch,
  CircularProgress,
  Autocomplete,
  Grid
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import axios from 'axios';
import api from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import { useSnackbar } from '../context/SnackbarContext';
import { Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const Calendar = () => {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const [interactions, setInteractions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [interactionTypes, setInteractionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('view');
  
  // 새 일정 상태
  const [formData, setFormData] = useState({
    customerId: '',
    type: '',
    date: (() => {
      const now = new Date();
      // 로컬 시간대로 설정 (KST +9)
      return new Date(now.getTime()).toISOString().slice(0, 16);
    })(),
    time: (() => {
      const now = new Date();
      return now.getHours().toString().padStart(2, '0') + ':' + 
            now.getMinutes().toString().padStart(2, '0');
    })(),
    notes: '',
    sale_result: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 모든 상호작용 데이터 가져오기
      const interactionsResponse = await api.get('/interactions');
      console.log('상호작용 데이터:', interactionsResponse.data);
      
      // 고객 목록 가져오기
      const customersResponse = await api.get('/customers');
      console.log('고객 목록:', customersResponse.data);
      
      // 상호작용 유형 가져오기
      const typesResponse = await api.get('/interactions/types');
      console.log('상호작용 유형:', typesResponse.data);
      
      setInteractions(interactionsResponse.data);
      setCustomers(customersResponse.data);
      setInteractionTypes(typesResponse.data);
    } catch (error) {
      console.error('데이터를 가져오는 중 오류가 발생했습니다:', error);
      // 더 자세한 오류 정보 로깅
      if (error.response) {
        // 서버 응답이 있는 경우 (상태 코드가 2xx가 아닌 경우)
        console.error('응답 데이터:', error.response.data);
        console.error('응답 상태:', error.response.status);
        console.error('응답 헤더:', error.response.headers);
      } else if (error.request) {
        // 요청이 이루어졌으나 응답을 받지 못한 경우
        console.error('요청 정보:', error.request);
      } else {
        // 요청 설정 중에 문제가 발생한 경우
        console.error('에러 메시지:', error.message);
      }
      showSnackbar('데이터를 가져오는 중 오류가 발생했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = (arg) => {
    const date = new Date(arg.date);
    // KST(UTC+9) 기준으로 현재 시간 설정
    const now = new Date();
    date.setHours(now.getHours(), now.getMinutes());
    
    setFormData({
      customerId: '',
      type: '',
      date: date.toISOString().slice(0, 16),
      time: now.getHours().toString().padStart(2, '0') + ':' + 
            now.getMinutes().toString().padStart(2, '0'),
      notes: '',
      sale_result: false
    });
    setViewMode('add');
    setDialogOpen(true);
  };

  const handleEventClick = (arg) => {
    // 이벤트에서 상호작용 데이터 가져오기
    const interaction = arg.event.extendedProps.interaction;
    
    // 고객 ID로 고객 정보 찾기
    const customer = customers.find(c => c.id === interaction.customer_id);
    
    const date = new Date(interaction.date);
    
    console.log('선택된 상호작용:', interaction);
    console.log('고객 ID 타입:', typeof interaction.customer_id);
    console.log('고객 ID 값:', interaction.customer_id);
    
    setSelectedEvent(interaction);
    setFormData({
      id: interaction.id,
      customerId: interaction.customer_id,
      type: interaction.type,
      date: date.toISOString().slice(0, 10),
      time: date.getHours().toString().padStart(2, '0') + ':' + 
            date.getMinutes().toString().padStart(2, '0'),
      notes: interaction.notes || '',
      sale_result: interaction.sale_result || false
    });
    setViewMode('view');
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleCustomerChange = (_, value) => {
    if (value && value.id) {
      setFormData({
        ...formData,
        customerId: value.id
      });
      console.log('선택된 고객:', value.name, '(ID:', value.id, ')'); // 디버깅용 로그
    } else {
      setFormData({
        ...formData,
        customerId: ''
      });
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDetailDialogOpen(false);
    setSelectedEvent(null);
    setViewMode('add');
    resetFormData();
  };

  const handleSubmit = async () => {
    try {
      // 필드 유효성 검사
      if (!formData.customerId) {
        showSnackbar('고객을 선택해주세요', 'error');
        return;
      }
      
      if (!formData.type) {
        showSnackbar('상호작용 유형을 선택해주세요', 'error');
        return;
      }

      // 로컬 시간대로 날짜+시간 조합
      let dateTime = formData.date;
      if (formData.time) {
        dateTime = `${formData.date}T${formData.time}:00`;
      }
      
      // 로컬 시간대 정보가 포함된 날짜 생성
      const localDate = new Date(dateTime);
      
      // ISO 문자열에서 Z(UTC 표시) 제거한 형식으로 변환
      const formattedDate = localDate.getFullYear() + '-' +
          String(localDate.getMonth() + 1).padStart(2, '0') + '-' +
          String(localDate.getDate()).padStart(2, '0') + 'T' +
          String(localDate.getHours()).padStart(2, '0') + ':' +
          String(localDate.getMinutes()).padStart(2, '0') + ':' +
          String(localDate.getSeconds()).padStart(2, '0');
      
      const interactionData = {
        customer_id: formData.customerId,
        type: formData.type,
        date: formattedDate,
        notes: formData.notes,
        sale_result: formData.sale_result,
        created_by: user.id,
      };
      
      console.log('전송할 데이터:', interactionData);
      
      if (selectedEvent) {
        // 기존 일정 수정
        await api.put(`/interactions/${selectedEvent.id}`, interactionData);
        showSnackbar('상호작용이 성공적으로 수정되었습니다', 'success');
      } else {
        // 새 일정 추가
        await api.post('/interactions', interactionData);
        showSnackbar('상호작용이 성공적으로 추가되었습니다', 'success');
      }
      
      handleCloseDialog();
      fetchData(); // 데이터 새로고침
    } catch (error) {
      console.error('상호작용 저장 중 오류가 발생했습니다:', error);
      showSnackbar('상호작용 저장 중 오류가 발생했습니다', 'error');
    }
  };

  const handleMarkComplete = async () => {
    if (!selectedEvent) return;
    
    try {
      await api.put(`/interactions/${selectedEvent.id}`, {
        ...selectedEvent,
        sale_result: true
      });
      showSnackbar('상호작용이 완료로 표시되었습니다', 'success');
      handleCloseDialog();
      fetchData(); // 데이터 새로고침
    } catch (error) {
      console.error('상호작용을 업데이트하는 중 오류가 발생했습니다:', error);
      showSnackbar('상호작용을 업데이트하는 중 오류가 발생했습니다', 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    
    try {
      await api.delete(`/interactions/${selectedEvent.id}`);
      showSnackbar('상호작용이 삭제되었습니다', 'success');
      handleCloseDialog();
      fetchData(); // 데이터 새로고침
    } catch (error) {
      console.error('상호작용을 삭제하는 중 오류가 발생했습니다:', error);
      showSnackbar('상호작용을 삭제하는 중 오류가 발생했습니다', 'error');
    }
  };

  // 상호작용을 캘린더 이벤트로 변환하는 함수
  const transformInteractionsToEvents = (interactions) => {
    return interactions.map(interaction => {
      // 고객 이름 찾기
      const customer = customers.find(c => c.id === interaction.customer_id);
      const customerName = customer ? customer.name : '알 수 없는 고객';
      
      // 상호작용 유형에 따른 색상 결정
      const backgroundColor = getTypeColor(interaction.type);
      
      return {
        id: interaction.id,
        title: `${customerName} - ${getTypeLabel(interaction.type)}`,
        start: new Date(interaction.date),
        backgroundColor: backgroundColor,
        borderColor: backgroundColor,
        interaction: interaction, // 원본 상호작용 데이터 저장
        extendedProps: {
          customerName: customerName,
          type: interaction.type,
          notes: interaction.notes,
          sale_result: interaction.sale_result
        }
      };
    });
  };

  // 상호작용 유형에 따른 색상 반환
  const getTypeColor = (type) => {
    switch (type) {
      case '전화':
        return '#4caf50'; // 녹색
      case '미팅':
        return '#2196f3'; // 파란색
      case '이메일':
        return '#ff9800'; // 주황색
      case '문자':
        return '#9c27b0'; // 보라색
      default:
        return '#757575'; // 기본 회색
    }
  };

  // 상호작용 유형 레이블 반환
  const getTypeLabel = (type) => {
    switch (type) {
      case '전화':
        return '전화';
      case '미팅':
        return '미팅';
      case '이메일':
        return '이메일';
      case '문자':
        return '문자';
      default:
        return type;
    }
  };

  const getCustomerName = (id) => {
    const customer = customers.find(c => c.id === id);
    return customer ? customer.name : '알 수 없는 고객';
  };

  // 폼 데이터 초기화
  const resetFormData = () => {
    // 현재 날짜와 시간을 로컬 시간대로 가져오기
    const now = new Date();
    const localDateStr = now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0');
    
    const localTimeStr = String(now.getHours()).padStart(2, '0') + ':' +
      String(now.getMinutes()).padStart(2, '0');
    
    setFormData({
      customerId: '',
      type: '전화',
      date: localDateStr,
      time: localTimeStr,
      notes: '',
      sale_result: false,
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography component="h1" variant="h4" color="primary" gutterBottom>
          일정 관리
        </Typography>
        
        <Box sx={{ height: 'calc(100vh - 200px)', mt: 3 }}>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
            }}
            initialView="dayGridMonth"
            events={transformInteractionsToEvents(interactions)}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            locale="ko"
            height="100%"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            eventDidMount={(info) => {
              // 툴팁 추가
              try {
                const tooltip = new Tooltip(info.el, {
                  title: `${info.event.extendedProps.customerName}<br/>
                         ${getTypeLabel(info.event.extendedProps.type)}<br/>
                         ${info.event.extendedProps.notes || ''}<br/>
                         ${info.event.extendedProps.sale_result ? '판매 성공' : ''}`,
                  placement: 'top',
                  trigger: 'hover',
                  container: 'body',
                  html: true
                });
              } catch (error) {
                console.warn('툴팁을 표시하는 중 오류가 발생했습니다:', error);
              }
            }}
          />
        </Box>
      </Paper>

      {/* 새 일정 추가 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{viewMode === 'add' ? '새 상호작용 추가' : '상호작용 수정'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              id="customer-select"
              options={customers}
              getOptionLabel={(option) => option.name}
              onChange={handleCustomerChange}
              value={formData.customerId ? customers.find(c => c.id == formData.customerId) || null : null}
              renderInput={(params) => (
                <TextField {...params} label="고객" fullWidth margin="normal" required />
              )}
            />
            
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="type-label">상호작용 유형</InputLabel>
              <Select
                labelId="type-label"
                id="type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                label="상호작용 유형"
              >
                <MenuItem value="전화">전화</MenuItem>
                <MenuItem value="미팅">미팅</MenuItem>
                <MenuItem value="이메일">이메일</MenuItem>
                <MenuItem value="문자">문자</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="date"
              label="날짜"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="time"
              label="시간"
              name="time"
              type="time"
              value={formData.time}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="notes"
              label="메모"
              name="notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={handleInputChange}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sale_result}
                  onChange={handleSwitchChange}
                  name="sale_result"
                  color="success"
                />
              }
              label="판매 성공"
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {viewMode === 'view' && (
            <Button onClick={handleDelete} color="error">
              삭제
            </Button>
          )}
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 일정 상세 보기 다이얼로그 */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle>{getCustomerName(selectedEvent.customer_id)} - {getTypeLabel(selectedEvent.type)}</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>날짜:</strong> {new Date(selectedEvent.date).toLocaleString('ko-KR')}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>판매 여부:</strong> {selectedEvent.sale_result ? '성공' : '없음'}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>메모:</strong>
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, minHeight: '100px' }}>
                  <Typography variant="body2">
                    {selectedEvent.notes || '메모가 없습니다.'}
                  </Typography>
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="primary">닫기</Button>
              <Button onClick={handleMarkComplete} color="success">판매 성공으로 표시</Button>
              <Button onClick={handleDelete} color="error">삭제</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default Calendar; 